import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const NewSignatureRequest = () => {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        document_id: '',
        title: '',
        message: '',
        expires_days: 30,
        signers: [{ name: '', email: '', role: 'SIGNER' }]
    });

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/documents/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setDocuments(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching documents:', err);
        }
    };

    const addSigner = () => {
        setFormData({
            ...formData,
            signers: [...formData.signers, { name: '', email: '', role: 'SIGNER' }]
        });
    };

    const removeSigner = (index) => {
        const newSigners = formData.signers.filter((_, i) => i !== index);
        setFormData({ ...formData, signers: newSigners });
    };

    const updateSigner = (index, field, value) => {
        const newSigners = [...formData.signers];
        newSigners[index] = { ...newSigners[index], [field]: value };
        setFormData({ ...formData, signers: newSigners });
    };

    const handleSubmit = async (e, sendImmediately = false) => {
        e.preventDefault();

        // Validation
        if (!formData.document_id) {
            alert('Please select a document');
            return;
        }
        if (!formData.title) {
            alert('Please enter a title');
            return;
        }
        if (formData.signers.some(s => !s.name || !s.email)) {
            alert('Please fill in all signer details');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/esignature/requests/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    document_id: parseInt(formData.document_id),
                    title: formData.title,
                    message: formData.message,
                    expires_days: formData.expires_days,
                    signers: formData.signers.map((s, i) => ({
                        ...s,
                        order: i + 1
                    })),
                    send_immediately: sendImmediately
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to create request');
            }

            const data = await response.json();
            navigate(`/esignature/requests/${data.id}`);
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/esignature" className="hover:text-blue-600">E-Signatures</Link>
                    <span className="mx-2">›</span>
                    <span>New Request</span>
                </nav>
                <h1 className="text-2xl font-bold text-gray-900">Request Signatures</h1>
                <p className="text-gray-600 mt-1">Send a document for electronic signature</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
                    {/* Document Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Document *
                        </label>
                        <select
                            required
                            value={formData.document_id}
                            onChange={(e) => setFormData({ ...formData, document_id: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                            <option value="">Choose a document</option>
                            {documents.map(doc => (
                                <option key={doc.id} value={doc.id}>
                                    {doc.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Request Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Request Title *
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            placeholder="e.g., NDA Agreement - John Smith"
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Message to Signers
                        </label>
                        <textarea
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            rows="3"
                            placeholder="Optional message that will be included in the email..."
                        ></textarea>
                    </div>

                    {/* Expiration */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expires In (Days)
                        </label>
                        <select
                            value={formData.expires_days}
                            onChange={(e) => setFormData({ ...formData, expires_days: parseInt(e.target.value) })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                            <option value={60}>60 days</option>
                            <option value={90}>90 days</option>
                        </select>
                    </div>

                    {/* Signers */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-medium text-gray-700">
                                Signers *
                            </label>
                            <button
                                type="button"
                                onClick={addSigner}
                                className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add Signer
                            </button>
                        </div>

                        <div className="space-y-4">
                            {formData.signers.map((signer, index) => (
                                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-medium text-gray-700">Signer #{index + 1}</span>
                                        {formData.signers.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeSigner(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <input
                                            type="text"
                                            placeholder="Full Name"
                                            value={signer.name}
                                            onChange={(e) => updateSigner(index, 'name', e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2"
                                            required
                                        />
                                        <input
                                            type="email"
                                            placeholder="Email Address"
                                            value={signer.email}
                                            onChange={(e) => updateSigner(index, 'email', e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2"
                                            required
                                        />
                                        <select
                                            value={signer.role}
                                            onChange={(e) => updateSigner(index, 'role', e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2"
                                        >
                                            <option value="SIGNER">Signer</option>
                                            <option value="APPROVER">Approver</option>
                                            <option value="WITNESS">Witness</option>
                                            <option value="CC">Receive Copy Only</option>
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-blue-800">
                                <p className="font-medium">How it works</p>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                    <li>Each signer will receive an email with a secure link</li>
                                    <li>They'll verify their identity with a code sent to their email</li>
                                    <li>Once verified, they can review and sign the document</li>
                                    <li>You'll be notified when all signatures are collected</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex-1 py-3 rounded-lg text-gray-700 font-medium border border-gray-300 ${loading ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'
                                }`}
                        >
                            Save as Draft
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSubmit(e, true)}
                            disabled={loading}
                            className={`flex-1 py-3 rounded-lg text-white font-medium ${loading
                                    ? 'bg-gray-400'
                                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                                }`}
                        >
                            {loading ? 'Sending...' : 'Send for Signature'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewSignatureRequest;
